<!-- 講座画面、メンバー一覧選択時のページ -->
<script setup lang="ts">
  import { PlanInfo } from "~/entities/PlanInfo";
  import PlanMemberList from "~/components/Organisms/PlanMemberList.vue";
  import PlanMemberOffer from "~/components/Organisms/PlanMemberOffer.vue";
  import PlanMemberStatistic from "~/components/Organisms/PlanMemberStatistic.vue";
  import PlanManagePriceList from "~/components/Organisms/PlanManagePriceList.vue";
  import PlanBulkDelivery from "~/components/Organisms/PlanBulkDelivery.vue";
  import PlanStepDelivery from "~/components/Organisms/PlanStepDelivery.vue";
  import type { DraggableItem } from "~/entities/DraggableItem";
  import { useHistoryStore } from "~/stores/HistoryStore";
  const historyStore = useHistoryStore();

  const planMemberList = ref<InstanceType<typeof PlanMemberList> | null>(null);
  const planMemberStatistic = ref<InstanceType<typeof PlanMemberStatistic> | null>(null);
  const planManagePriceList = ref<InstanceType<typeof PlanManagePriceList> | null>(null);
  const planMemberOffer = ref<InstanceType<typeof PlanMemberOffer> | null>(null);
  const planBulkDelivery = ref<InstanceType<typeof PlanBulkDelivery> | null>(null);
  const planStepDelivery = ref<InstanceType<typeof PlanStepDelivery> | null>(null);

  interface Props {
    planInfo: PlanInfo;
    parentWidth: number; // 横スクロールがうまくいかないので親要素の幅を取得し、それを元にwidthを決めている
  }
  const props = withDefaults(defineProps<Props>(), {});

  const isTablet = isTabletAndSpDisplayRef();
  const showDetail = ref(false);

  const selectedId = ref<string>(String(useRoute().query.content || "list"));

  const memberWrapperRef = ref<HTMLDivElement | undefined>(undefined);
  const memberWrapperHeight = computed<string | null>(() => {
    if (isTablet.value && showDetail.value) return "inherit";
    const rect = memberWrapperRef.value?.getBoundingClientRect();
    if (rect) return `calc(100svh - ${rect.y > 0 ? rect.y : 134}px)`;
    return null;
  });
  const memberDetailWidth = computed<string | null>(() => {
    if (isTablet.value) {
      return "100%";
    }
    // 横スクロールがうまくいかないので親要素の幅を取得し、それを元にwidthを決めている
    if (props.parentWidth) {
      const width = Math.min(props.parentWidth, 1280);
      return `${width - 248}px`;
    }
    return null;
  });

  const onClickItem = (id: string) => {
    selectedId.value = id;
    showDetail.value = true;

    historyStore.to({ query: { ...useRoute().query, content: id, showDetail: "true" } });
  };

  const menuItems = () => {
    const items = [
      {
        id: "list",
        label: "会員一覧",
        canView: true,
        hasUnread: false,
        imgPath: "assets/images/icon/member-list.svg",
        isUserIcon: false,
        isOwner: false,
        onClick: onClickItem,
      },
      {
        id: "statistic",
        label: "統計",
        canView: true,
        hasUnread: false,
        imgPath: "assets/images/icon/member-statistic.svg",
        isUserIcon: false,
        isOwner: false,
        onClick: onClickItem,
      },
    ];
    items.push({
      id: "price",
      label: "プラン設定",
      canView: true,
      hasUnread: false,
      imgPath: "assets/images/icon/member-price.webp",
      isUserIcon: false,
      isOwner: false,
      onClick: onClickItem,
    });
    items.push({
      id: "offer",
      label: "ワンタイムオファー",
      canView: true,
      hasUnread: false,
      imgPath: "assets/images/icon/member-offer.webp",
      isUserIcon: false,
      isOwner: false,
      onClick: onClickItem,
    });
    if (featureBulkDelivery()) {
      items.push({
        id: "bulk-delivery",
        label: "一斉配信",
        canView: true,
        hasUnread: false,
        imgPath: "assets/images/icon/bulk-delivery.webp",
        isUserIcon: false,
        isOwner: false,
        onClick: onClickItem,
      });
    }
    if (featureStepDelivery()) {
      items.push({
        id: "step-delivery",
        label: "ステップ配信",
        canView: true,
        hasUnread: false,
        imgPath: "assets/images/icon/step-delivery.webp",
        isUserIcon: false,
        isOwner: false,
        onClick: onClickItem,
      });
    }
    return items;
  };

  // ローカルメニュー
  const localMenus = ref<DraggableItem[]>(menuItems());

  // データ取得
  const fetchContents = (tabValue: string) => {
    switch (tabValue) {
      case "list":
        planMemberList.value?.fetch();
        break;
      case "statistic":
        planMemberStatistic.value?.fetch();
        break;
      case "price":
        planManagePriceList.value?.fetch();
        break;
      case "offer":
        planMemberOffer.value?.fetch();
        break;
      case "bulk-delivery":
        planBulkDelivery.value?.fetch();
        break;
      case "step-delivery":
        planStepDelivery.value?.fetch();
        break;
    }
  };

  const fetch = () => {
    fetchContents(selectedId.value);
  };

  watch(
    () => useRoute().query.planId,
    () => {
      fetch();
    },
  );

  watch(
    () => {
      return selectedId.value;
    },
    (newValue, _oldValue) => {
      fetchContents(newValue);
    },
  );

  watch(
    () => useRoute().query.content,
    val => {
      selectedId.value = String(val) || "list";
    },
  );

  onMounted(() => {
    fetch();
  });

  defineExpose({
    fetch,
  });
</script>
<template>
  <div ref="memberWrapperRef" class="member_wrapper">
    <MoleculesContainerMenuList :class="{ 'd-none': isTablet && showDetail }">
      <MoleculesDraggableList
        v-model:items="localMenus"
        :selected-id="!isTablet ? selectedId : ''"
        :can-drag="false"
      ></MoleculesDraggableList>
    </MoleculesContainerMenuList>

    <div v-show="selectedId === 'list'" class="member_detail" :class="{ 'd-none': isTablet && !showDetail }">
      <OrganismsPlanMemberList ref="planMemberList" :plan-info="planInfo" @on-back="showDetail = false" />
    </div>
    <div v-show="selectedId === 'statistic'" class="member_detail" :class="{ 'd-none': isTablet && !showDetail }">
      <OrganismsPlanMemberStatistic ref="planMemberStatistic" :plan-info="planInfo" @on-back="showDetail = false" />
    </div>
    <div v-show="selectedId === 'price'" class="member_detail" :class="{ 'd-none': isTablet && !showDetail }">
      <OrganismsPlanManagePriceList ref="planManagePriceList" :plan-info="planInfo" @on-back="showDetail = false" />
    </div>
    <div v-show="selectedId === 'offer'" class="member_detail" :class="{ 'd-none': isTablet && !showDetail }">
      <OrganismsPlanMemberOffer ref="planMemberOffer" :plan-info="planInfo" @on-back="showDetail = false" />
    </div>
    <div
      v-if="featureBulkDelivery()"
      v-show="selectedId === 'bulk-delivery'"
      class="member_detail"
      :class="{ 'd-none': isTablet && !showDetail }"
    >
      <OrganismsPlanBulkDelivery ref="planBulkDelivery" :plan-info="planInfo" @on-back="showDetail = false" />
    </div>
    <div
      v-if="featureStepDelivery()"
      v-show="selectedId === 'step-delivery'"
      class="member_detail"
      :class="{ 'd-none': isTablet && !showDetail }"
    >
      <OrganismsPlanStepDelivery ref="planStepDelivery" :plan-info="planInfo" @on-back="showDetail = false" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @import "assets/media.scss";
  .member_wrapper {
    display: table;
    max-width: 1280px;
    width: 100%;
    height: v-bind("memberWrapperHeight");
    position: relative;
    @include display_sp {
      width: 100%;
      display: inline-table;
    }
  }
  .member_detail {
    display: table-cell;
    width: v-bind("memberDetailWidth");
    max-width: v-bind("memberDetailWidth");
    height: 100%;
    vertical-align: top;
    position: relative;
  }

  @include display_tablet {
    .member_wrapper {
      width: 100vw;
      height: initial !important;
      .member_detail {
        position: fixed;
        width: 100vw;
        height: 100svh;
        z-index: 1000;
        top: 0;
        left: 0;
        background: black;
        overflow-y: scroll;
      }
    }
  }
</style>
